import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Input,
  inject,
  OnInit,
  SecurityContext,
  PLATFORM_ID,
  OnChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { filter, interval } from 'rxjs';

import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import {
  Breadcrumbs,
  CtaButton,
  FillColor,
  HeroCarousel,
  HeroCarouselType,
  InlineImage,
} from '@digital-platform/rs-cms/domain';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { BreadcrumbsPtypeComponent } from '../breadcrumbs-ptype/breadcrumbs-ptype.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { InlineImageComponent } from '../inline-image/inline-image.component';

@Component({
  selector: 'digital-platform-hero-carousel',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsPtypeComponent,
    CtaButtonComponent,
    InlineImageComponent,
  ],
  templateUrl: './hero-carousel.component.html',
  styleUrls: ['./hero-carousel.component.scss'],
})
export class HeroCarouselComponent
  extends BasePTypeComponent
  implements HeroCarousel, OnInit, OnChanges
{
  public override type = 'heroCarousel' as const;

  public sanitizer = inject(DomSanitizer);
  public showBreadcrumbs = false;

  public activeIndex = 0;
  public playing = true;
  public fillColorToBgColor = '';

  public hasControls = false;
  public hasAboveHeader = false;
  public hasHeader = false;
  public hasCaption = false;
  public hasCtaButton = false;

  public emptyBreadcrumbs: Breadcrumbs = {
    id: -1,
    uuid: '',
    revisionId: -1,
    type: 'breadcrumbs' as const,
    status: true,
    parentType: '',
    behaviorSettings: {},
    //
    hideHome: false,
    nestedBreadcrumbItem: [],
  };

  @Input() heroCarouselType: HeroCarouselType = {};
  _textAboveTitle: string[] = [];
  get textAboveTitle(): string[] {
    return this._textAboveTitle;
  }
  @Input() set textAboveTitle(value: string[] | null) {
    this._textAboveTitle = value || [];
  }
  @Input() richTextTitle: string[] = [];
  @Input() description: string[] = [];
  @Input() paragraphs: CtaButton[] = [];
  @Input() swooshOverlayColor: FillColor = {};
  //
  @Input() translucentImage: InlineImage[] = []; // Content team calls this a 'duo-tone' image.
  @Input() carouselImage: InlineImage[] = [];
  //
  @Input() overlayAlert1: string | null = '';
  @Input() overlayAlert2: string | null = '';
  @Input() showContactEmergency = false;
  //
  @Input() mobileBreadcrumbs: Breadcrumbs[] = [this.emptyBreadcrumbs];

  public mobileTranslucentImage: InlineImage[] = [];

  private analyticsService = inject(AnalyticsService);
  private PLATFORM_ID = inject(PLATFORM_ID);

  private timer = interval(4000).pipe(filter(() => this.playing));

  public ngOnInit() {
    const fillColorToBgColor: string =
      this?.swooshOverlayColor?.cssColorName || '';
    this.fillColorToBgColor = fillColorToBgColor.replace('fill', 'bg');

    this.showBreadcrumbs = this.mobileBreadcrumbs.length > 0;

    if (isPlatformBrowser(this.PLATFORM_ID)) {
      // start the carousel on the browser side only
      this.timer.subscribe(() => this.incrementIndex());
    }

    this.validateContentArrays();
  }

  public ngOnChanges() {
    if (this.translucentImage) {
      this.mobileTranslucentImage = this.translucentImage.reduce<InlineImage[]>(
        (acc, image) => {
          const adjusted = this.smRangeImageGradientAdjustor(image);

          if (adjusted !== undefined) {
            acc.push(adjusted);
          }

          return acc;
        },
        []
      );
    } else {
      this.mobileTranslucentImage = [];
    }

    this.hasControls = this.carouselImage.length > 1 ? true : false;
    this.hasAboveHeader =
      Array.isArray(this.textAboveTitle) && this.textAboveTitle[0]
        ? true
        : false;
    this.hasHeader =
      Array.isArray(this.richTextTitle) && this.richTextTitle[0] ? true : false;
    this.hasCaption = this.description[0] ? true : false;
    this.hasCtaButton = this.paragraphs?.length ? true : false;
  }

  get isL2() {
    return this.heroCarouselType?.carouselType === 'L2';
  }

  get isL1() {
    return this.heroCarouselType?.carouselType === 'L1';
  }

  // Fills in gaps if text or buttons are missing in array
  // by duplicating previous item
  validateContentArrays(): void {
    const slideCount =
      this.carouselImage.length > 0
        ? this.carouselImage.length
        : this.translucentImage.length;

    // Could be string or array of strings
    if (!Array.isArray(this.richTextTitle)) {
      const rtVal = this.richTextTitle;
      this.richTextTitle = [rtVal];
    }

    for (let i = 1; i < slideCount; i++) {
      this.textAboveTitle[i] =
        this.textAboveTitle[i] || this.textAboveTitle[i - 1];
      this.richTextTitle[i] =
        this.richTextTitle[i] || this.richTextTitle[i - 1];
      this.description[i] = this.description[i] || this.description[i - 1];
      this.paragraphs[i] = this.paragraphs[i] || this.paragraphs[i - 1];
    }
  }

  smRangeImageGradientAdjustor(
    inlineImage: InlineImage | undefined
  ): InlineImage | undefined {
    // use mobile gradient direction and opacity start/end settings in the sm range
    // clone the object to prevent mutation
    const fixedInlineImage: InlineImage | undefined = inlineImage
      ? { ...inlineImage }
      : inlineImage;

    if (inlineImage && fixedInlineImage) {
      fixedInlineImage.endOpacityPercent = inlineImage.endOpacityPercent;
      fixedInlineImage.endPositionPercent = inlineImage.endPositionPercentMobil;
      fixedInlineImage.gradientDirection = inlineImage.gradientDirectionMobile;
      fixedInlineImage.startOpacityPercent =
        inlineImage.startOpacityPercentMobi;
      fixedInlineImage.startPositionPercent =
        inlineImage.startPositionPercentMob;
    }

    return fixedInlineImage;
  }

  stringOrNullToString(cssClass: string | null = ''): string {
    const retVal: string = cssClass || '';
    return this.sanitizer.sanitize(SecurityContext.NONE, retVal) || '';
  }

  getCtaButton(): CtaButton {
    let retval = {} as CtaButton;
    if (this.paragraphs?.length > 0) {
      retval = this.paragraphs[this.activeIndex];
    }

    return retval || ({} as CtaButton);
  }

  incrementIndex() {
    const nextIndex = this.activeIndex + 1;
    this.activeIndex = nextIndex === this.carouselImage.length ? 0 : nextIndex;
  }

  selectSlide(index: number) {
    this.activeIndex = index;
  }

  playPause() {
    if (this.playing) {
      this.analyticsService.trackHeroCarouselPause();
      this.playing = false;
      return;
    }
    this.analyticsService.trackHeroCarouselStart();
    this.playing = true;
  }
}
